import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, FolderOpen, Upload, FileText, Pencil, Trash2, X, AlertTriangle, HelpCircle, Search, Download, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SYSTEM_VARIABLES = [
  { name: 'ANO_ATUAL', desc: 'Ano corrente (ex: 2026)', cat: 'Datas' },
  { name: 'CLIENTE_BAIRRO', desc: 'Bairro do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_CEP', desc: 'CEP do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_CIDADE', desc: 'Cidade do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_CIDADE_UF', desc: 'Cidade/UF do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_CNPJ', desc: 'CNPJ do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_COMPLEMENTO', desc: 'Complemento do endereço', cat: 'Cliente' },
  { name: 'CLIENTE_ENDERECO', desc: 'Endereço completo do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_ESFERA', desc: 'Esfera governamental (Federal, Estadual, Municipal)', cat: 'Cliente Público' },
  { name: 'CLIENTE_LOGRADOURO', desc: 'Logradouro do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_NOME_FANTASIA', desc: 'Nome fantasia do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_NUMERO', desc: 'Número do endereço', cat: 'Cliente' },
  { name: 'CLIENTE_PODER', desc: 'Poder governamental (Executivo, Legislativo, Judiciário)', cat: 'Cliente Público' },
  { name: 'CLIENTE_RAZAO_SOCIAL', desc: 'Razão social do cliente', cat: 'Cliente' },
  { name: 'CLIENTE_TIPO', desc: 'Tipo do cliente (Público/Privado)', cat: 'Cliente' },
  { name: 'CLIENTE_UF', desc: 'UF do cliente', cat: 'Cliente' },
  { name: 'CNPJ_ORGAO', desc: 'CNPJ do órgão contratante', cat: 'Cliente Público' },
  { name: 'CONTRATO_NUMERO', desc: 'Número do contrato', cat: 'Contrato / Processo' },
  { name: 'DATA_CRIACAO', desc: 'Data de criação (DD/MM/AAAA)', cat: 'Datas' },
  { name: 'DATA_CURTA', desc: 'Data curta (DD/MM/AAAA)', cat: 'Datas' },
  { name: 'DATA_EXTENSO', desc: 'Data por extenso (ex: 1 de abril de 2026)', cat: 'Datas' },
  { name: 'DATA_HOJE', desc: 'Data de hoje (DD/MM/AAAA)', cat: 'Datas' },
  { name: 'DIA_ATUAL', desc: 'Dia corrente', cat: 'Datas' },
  { name: 'DOCUMENTO_NUMERO', desc: 'Número do documento', cat: 'Documento' },
  { name: 'DOCUMENTO_TIPO', desc: 'Tipo do documento (TR, ETP, Proposta etc.)', cat: 'Documento' },
  { name: 'DOCUMENTO_TITULO', desc: 'Título do documento', cat: 'Documento' },
  { name: 'DOCUMENTO_VERSAO', desc: 'Versão atual do documento', cat: 'Documento' },
  { name: 'EMPRESA_CNPJ', desc: 'CNPJ da ASI Company', cat: 'Empresa' },
  { name: 'EMPRESA_EMAIL', desc: 'E-mail da ASI Company', cat: 'Empresa' },
  { name: 'EMPRESA_ENDERECO', desc: 'Endereço da ASI Company', cat: 'Empresa' },
  { name: 'EMPRESA_NOME', desc: 'Nome da empresa (ASI Company Tecnologia S.A.)', cat: 'Empresa' },
  { name: 'EMPRESA_RAZAO_SOCIAL', desc: 'Razão social completa', cat: 'Empresa' },
  { name: 'EMPRESA_TELEFONE', desc: 'Telefone da ASI Company', cat: 'Empresa' },
  { name: 'EMPRESA_WEBSITE', desc: 'Website da ASI Company', cat: 'Empresa' },
  { name: 'ENDERECO_ORGAO', desc: 'Endereço do órgão contratante', cat: 'Cliente Público' },
  { name: 'EXERCICIO_ORCAMENTARIO', desc: 'Exercício orçamentário (ano)', cat: 'Contrato / Processo' },
  { name: 'FISCAL_ADMINISTRATIVO', desc: 'Nome do fiscal administrativo', cat: 'Gestão / Fiscalização' },
  { name: 'FISCAL_TECNICO', desc: 'Nome do fiscal técnico', cat: 'Gestão / Fiscalização' },
  { name: 'FORMA_PAGAMENTO', desc: 'Forma de pagamento', cat: 'Comercial' },
  { name: 'FORNECEDOR_CNPJ', desc: 'CNPJ do fornecedor (alias EMPRESA_CNPJ)', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_EMAIL', desc: 'E-mail do fornecedor (alias EMPRESA_EMAIL)', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_ENDERECO', desc: 'Endereço do fornecedor (alias EMPRESA_ENDERECO)', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_NOME', desc: 'Nome do fornecedor (alias EMPRESA_NOME)', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_RAZAO_SOCIAL', desc: 'Razão social do fornecedor (alias EMPRESA_RAZAO_SOCIAL)', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_TELEFONE', desc: 'Telefone do fornecedor', cat: 'Fornecedor (alias)' },
  { name: 'FORNECEDOR_WEBSITE', desc: 'Website do fornecedor', cat: 'Fornecedor (alias)' },
  { name: 'GESTOR_CONTRATO', desc: 'Nome do gestor do contrato', cat: 'Gestão / Fiscalização' },
  { name: 'ITEM_N_DESCRICAO', desc: 'Descrição do item N (N=literal, para tabela com 1 linha)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_NOME', desc: 'Nome do produto/serviço do item N (N=literal)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_QTD', desc: 'Quantidade do item N (N=literal)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_UNIDADE', desc: 'Unidade de medida do item N (N=literal)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_VALOR_TOTAL', desc: 'Valor total do item N (N=literal)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_VALOR_UNITARIO', desc: 'Valor unitário do item N (N=literal)', cat: 'Itens (automático)' },
  { name: 'ITEM_1_DESCRICAO', desc: 'Descrição do item 1 (ex: ITEM_1_DESCRICAO)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_NOME', desc: 'Nome do produto/serviço do item N', cat: 'Itens (automático)' },
  { name: 'ITEM_N_QTD', desc: 'Quantidade do item N', cat: 'Itens (automático)' },
  { name: 'ITEM_N_UNIDADE', desc: 'Unidade de medida do item N', cat: 'Itens (automático)' },
  { name: 'ITEM_N_VALOR_TOTAL', desc: 'Valor total do item N (R$)', cat: 'Itens (automático)' },
  { name: 'ITEM_N_VALOR_UNITARIO', desc: 'Valor unitário do item N (R$)', cat: 'Itens (automático)' },
  { name: 'ITENS_QTD', desc: 'Quantidade total de itens', cat: 'Itens (automático)' },
  { name: 'LOCAL_EXECUCAO', desc: 'Local de execução', cat: 'Projeto / Execução' },
  { name: 'MES_ATUAL', desc: 'Mês corrente por extenso', cat: 'Datas' },
  { name: 'MODALIDADE_LICITACAO', desc: 'Modalidade da licitação', cat: 'Contrato / Processo' },
  { name: 'NUMERO_DOCUMENTO', desc: 'Número do documento (alias DOCUMENTO_NUMERO)', cat: 'Documento' },
  { name: 'ORGAO_CONTRATANTE', desc: 'Nome do órgão contratante', cat: 'Cliente Público' },
  { name: 'ORGAO_NOME', desc: 'Nome do órgão', cat: 'Cliente Público' },
  { name: 'PRAZO_ENTREGA', desc: 'Prazo de entrega', cat: 'Comercial' },
  { name: 'PRAZO_EXECUCAO', desc: 'Prazo de execução', cat: 'Projeto / Execução' },
  { name: 'PRAZO_VIGENCIA', desc: 'Prazo de vigência', cat: 'Projeto / Execução' },
  { name: 'PROCESSO_NUMERO', desc: 'Número do processo', cat: 'Contrato / Processo' },
  { name: 'RESPONSAVEL_CARGO', desc: 'Cargo do responsável', cat: 'Responsável' },
  { name: 'RESPONSAVEL_EMAIL', desc: 'E-mail do responsável', cat: 'Responsável' },
  { name: 'RESPONSAVEL_NOME', desc: 'Nome do responsável', cat: 'Responsável' },
  { name: 'TABELA_ITENS_TEXTO', desc: 'Tabela de itens em formato texto', cat: 'Itens (automático)' },
  { name: 'TITULO_DOCUMENTO', desc: 'Título do documento (alias DOCUMENTO_TITULO)', cat: 'Documento' },
  { name: 'VALIDADE_PROPOSTA', desc: 'Validade da proposta (padrão: 60 dias)', cat: 'Comercial' },
  { name: 'VALOR_ESTIMADO', desc: 'Valor estimado', cat: 'Projeto / Execução' },
  { name: 'VALOR_TOTAL', desc: 'Valor total do documento', cat: 'Projeto / Execução' },
  { name: 'VALOR_TOTAL_ITENS', desc: 'Soma dos valores totais dos itens (R$)', cat: 'Itens (automático)' },
];

const TutorialContent = () => {
  const [varSearch, setVarSearch] = useState('');

  const filteredVars = SYSTEM_VARIABLES.filter(v =>
    !varSearch ||
    v.name.toLowerCase().includes(varSearch.toLowerCase()) ||
    v.desc.toLowerCase().includes(varSearch.toLowerCase()) ||
    v.cat.toLowerCase().includes(varSearch.toLowerCase())
  );

  const categories = [...new Set(filteredVars.map(v => v.cat))];

  return (
    <div className="space-y-4 text-sm text-[#111111]">
      <div className="bg-[#F7F7F8] rounded-lg p-4">
        <h4 className="font-semibold mb-2">1. Crie o documento no Word</h4>
        <p className="text-[#6E6E73]">Use o Microsoft Word ou Google Docs (exportando como .docx) para criar o layout do seu documento com a formatação desejada.</p>
      </div>
      <div className="bg-[#F7F7F8] rounded-lg p-4">
        <h4 className="font-semibold mb-2">2. Insira variáveis com dupla chave</h4>
        <p className="text-[#6E6E73]">Onde quiser que dados sejam preenchidos automaticamente, use o formato:</p>
        <code className="block bg-white border border-[#E5E5EA] rounded p-2 mt-2 text-[#AF2628]">{"{{NOME_DA_VARIAVEL}}"}</code>
        <p className="text-[#6E6E73] mt-2">Exemplo: <code className="bg-white px-1 rounded">{"{{CLIENTE_RAZAO_SOCIAL}}"}</code>, <code className="bg-white px-1 rounded">{"{{DATA_EXTENSO}}"}</code></p>
      </div>
      <div className="bg-[#F7F7F8] rounded-lg p-4">
        <h4 className="font-semibold mb-2">3. Variáveis de sistema disponíveis ({SYSTEM_VARIABLES.length})</h4>
        <div className="mt-2 mb-3">
          <div className="flex items-center gap-2 bg-white border border-[#E5E5EA] rounded-lg px-3 py-1.5">
            <Search className="w-4 h-4 text-[#AEAEB2] flex-shrink-0" />
            <input
              type="text"
              value={varSearch}
              onChange={(e) => setVarSearch(e.target.value)}
              placeholder="Buscar variável..."
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid="tutorial-variable-search"
            />
            {varSearch && (
              <button onClick={() => setVarSearch('')} className="text-[#AEAEB2] hover:text-[#6E6E73]">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {varSearch && <p className="text-xs text-[#AEAEB2] mt-1">{filteredVars.length} resultado{filteredVars.length !== 1 ? 's' : ''}</p>}
        </div>
        <div className="max-h-72 overflow-y-auto border border-[#E5E5EA] rounded-lg bg-white">
          {categories.map((cat) => (
            <div key={cat}>
              <div className="sticky top-0 bg-[#F7F7F8] px-3 py-1.5 border-b border-[#E5E5EA]">
                <span className="text-xs font-semibold text-[#6E6E73] uppercase tracking-wide">{cat}</span>
              </div>
              {filteredVars.filter(v => v.cat === cat).map((v) => (
                <div key={v.name} className="flex items-start gap-2 px-3 py-1.5 border-b border-[#F2F2F7] last:border-b-0 hover:bg-[#FAFAFA]">
                  <code className="text-[#AF2628] text-xs font-mono whitespace-nowrap">{`{{${v.name}}}`}</code>
                  <span className="text-xs text-[#6E6E73]">{v.desc}</span>
                </div>
              ))}
            </div>
          ))}
          {filteredVars.length === 0 && (
            <p className="text-center text-xs text-[#AEAEB2] py-4">Nenhuma variável encontrada</p>
          )}
        </div>
      </div>
      <div className="bg-[#F7F7F8] rounded-lg p-4">
        <h4 className="font-semibold mb-2">4. Variáveis personalizadas</h4>
        <p className="text-[#6E6E73]">Além das variáveis de sistema, você pode criar variáveis personalizadas diretamente no editor de variáveis do documento. Basta acessar o documento criado e clicar em "Editar Variáveis" para adicionar novas.</p>
      </div>
      <div className="bg-[#FFF1F0] rounded-lg p-4 border border-[#FFCCC7]">
        <h4 className="font-semibold mb-2">Importante</h4>
        <ul className="list-disc list-inside text-[#6E6E73] space-y-1">
          <li>Use SEMPRE dupla chave: {"{{VAR}}"} e não {"{VAR}"}</li>
          <li>Nomes em MAIÚSCULAS com underscore: {"{{NOME_COMPLETO}}"}</li>
          <li>Para itens, substitua N pelo número: {"{{ITEM_1_NOME}}"}, {"{{ITEM_2_QTD}}"}</li>
          <li>Não quebre a variável em múltiplas formatações no Word</li>
          <li>Salve como .docx (não .doc nem .pdf)</li>
        </ul>
      </div>
    </div>
  );
};

const EMPTY_FORM = { name: '', tipo: 'Proposta Comercial', categoria: '', descricao: '' };

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [file, setFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setFormData({ ...EMPTY_FORM });
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      tipo: template.tipo || 'Proposta Comercial',
      categoria: template.categoria || '',
      descricao: template.descricao || ''
    });
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.docx')) {
        toast.error('Por favor, selecione um arquivo .docx');
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadModelTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates/download-model-template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ASI_Modelo_Padrao.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Modelo Padrão baixado com sucesso');
    } catch (error) {
      toast.error('Erro ao baixar o Modelo Padrão');
    }
  };

  const _openPreviewBlob = (blobData, title) => {
    const blob = new Blob([blobData], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewOpen(true);
  };

  const _handlePreviewError = async (error) => {
    let errorDetail = null;
    try {
      const text = await error.response?.data?.text();
      errorDetail = text ? JSON.parse(text).detail : null;
    } catch {
      errorDetail = null;
    }
    if (errorDetail && typeof errorDetail === 'object' && Array.isArray(errorDetail.errors)) {
      toast.error(errorDetail.message || 'Prévia indisponível', {
        description: errorDetail.errors.join('\n\n'),
        duration: 12000,
      });
    } else if (errorDetail && typeof errorDetail === 'string') {
      toast.error(errorDetail);
    } else {
      toast.error('Erro ao gerar prévia do modelo');
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Selecione um arquivo .docx para visualizar a prévia');
      return;
    }
    setPreviewLoading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await axios.post(`${API_URL}/templates/preview`, data, {
        withCredentials: true,
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      _openPreviewBlob(response.data, file.name);
    } catch (error) {
      await _handlePreviewError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewExisting = async (template) => {
    setPreviewLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/templates/${template.template_id}/preview`,
        { withCredentials: true, responseType: 'blob' }
      );
      _openPreviewBlob(response.data, template.name);
    } catch (error) {
      await _handlePreviewError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingTemplate && !file) {
      toast.error('Selecione um arquivo .docx');
      return;
    }
    if (uploading) return;
    setUploading(true);

    const data = new FormData();
    data.append('name', formData.name);
    data.append('tipo', formData.tipo);
    if (formData.categoria) data.append('categoria', formData.categoria);
    if (formData.descricao) data.append('descricao', formData.descricao);
    if (file) data.append('file', file);

    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/templates/${editingTemplate.template_id}`, data, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Modelo atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/templates`, data, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Modelo criado com sucesso');
      }
      setDialogOpen(false);
      setEditingTemplate(null);
      setFormData({ ...EMPTY_FORM });
      setFile(null);
      loadTemplates();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        toast.error(detail.message || 'Modelo DOCX inválido', {
          description: detail.errors.join('\n\n'),
          duration: 12000,
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar modelo');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`, { withCredentials: true });
      toast.success('Modelo excluído com sucesso');
      setDeleteTarget(null);
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir modelo');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) { setEditingTemplate(null); setFormData({ ...EMPTY_FORM }); setFile(null); }
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Biblioteca de Modelos</h1>
          <p className="text-[#6E6E73]">Templates de documentos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadModelTemplate} data-testid="download-model-template-button">
            <Download className="w-4 h-4 mr-2" />
            Baixar Modelo Padrão
          </Button>
          <Button variant="outline" onClick={() => setTutorialOpen(true)} data-testid="tutorial-button">
            <HelpCircle className="w-4 h-4 mr-2" />
            Tutorial
          </Button>
          <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-template-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Modelo
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl p-6">
        {templates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div key={template.template_id} className="border border-[#E5E5EA] rounded-lg p-4 hover:shadow-md hover:border-[#AF2628] transition-all" data-testid={`template-card-${template.template_id}`}>
                <div className="flex items-start gap-3">
                  <div className="bg-[#F0FDF4] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#111111] truncate">{template.name}</h3>
                    <p className="text-xs text-[#6E6E73] mt-1">{template.tipo}</p>
                    {template.descricao && <p className="text-xs text-[#AEAEB2] mt-1 line-clamp-2">{template.descricao}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {template.categoria && <span className="text-xs bg-[#F7F7F8] px-2 py-1 rounded">{template.categoria}</span>}
                      <span className="text-xs text-[#AEAEB2]">{template.variables?.length || 0} variáveis</span>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => handlePreviewExisting(template)} className="text-xs" data-testid={`preview-template-${template.template_id}`}>
                        <Eye className="w-3 h-3 mr-1" />Prévia
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(template)} className="text-xs" data-testid={`edit-template-${template.template_id}`}>
                        <Pencil className="w-3 h-3 mr-1" />Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleteTarget(template)} className="text-xs text-red-600 hover:text-red-700" data-testid={`delete-template-${template.template_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FolderOpen className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73] mb-4">Nenhum modelo cadastrado</p>
            <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]">
              <Plus className="w-4 h-4 mr-2" />Criar Primeiro Modelo
            </Button>
          </div>
        )}
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Modelo' : 'Novo Modelo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
            <div>
              <Label>Nome do Modelo *</Label>
              <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Proposta Comercial TI" className="mt-1" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={formData.tipo} onValueChange={(val) => setFormData({ ...formData, tipo: val })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Proposta Comercial">Proposta Comercial</SelectItem>
                  <SelectItem value="ETP">ETP</SelectItem>
                  <SelectItem value="TR">TR</SelectItem>
                  <SelectItem value="Edital">Edital</SelectItem>
                  <SelectItem value="Documento Auxiliar">Documento Auxiliar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoria</Label>
              <Input value={formData.categoria} onChange={(e) => setFormData({ ...formData, categoria: e.target.value })} placeholder="Ex: TI - Software" className="mt-1" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} placeholder="Breve descrição do modelo" className="mt-1" />
            </div>
            <div>
              <Label>Arquivo .docx {!editingTemplate && '*'}</Label>
              <div className="mt-1">
                {/* Show current file when editing */}
                {editingTemplate && !file && (
                  <div className="mb-3 bg-[#F7F7F8] border border-[#E5E5EA] rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#AF2628]" />
                        <div>
                          <p className="text-sm font-medium text-[#111111]">{editingTemplate.file_name || 'Arquivo atual'}</p>
                          <p className="text-xs text-[#6E6E73]">{editingTemplate.variables?.length || 0} variáveis detectadas</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {editingTemplate.file_path && (
                          <a
                            href={`${API_URL}/templates/${editingTemplate.template_id}/download`}
                            className="text-xs text-[#007AFF] hover:underline flex items-center gap-1"
                            data-testid="download-template-file"
                          >
                            <Download className="w-3 h-3" /> Baixar
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#AEAEB2] mt-2">Selecione um novo arquivo abaixo para substituir</p>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".docx" onChange={handleFileChange}
                  required={!editingTemplate}
                  className="block w-full text-sm text-[#6E6E73] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#F7F7F8] file:text-[#111111] hover:file:bg-[#E5E5EA] file:cursor-pointer cursor-pointer border border-[#E5E5EA] rounded-lg p-2"
                  data-testid="template-file-input"
                />
                {file && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#34C759]">
                    <FileText className="w-4 h-4" />
                    <span>{file.name}</span>
                    <span className="text-[#6E6E73]">({(file.size / 1024).toFixed(1)} KB)</span>
                    <button type="button" onClick={clearFile} className="ml-1 text-red-500 hover:text-red-700" data-testid="clear-file-button">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <p className="mt-2 text-xs text-[#6E6E73]">
                  {"Use variáveis no formato "}
                  <code className="bg-[#F7F7F8] px-1 rounded">{"{{NOME_VARIAVEL}}"}</code>
                  {" no documento"}
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)} disabled={uploading}>Cancelar</Button>
              <Button
                type="button"
                variant="outline"
                onClick={handlePreview}
                disabled={uploading || previewLoading || !file}
                data-testid="template-preview-button"
              >
                {previewLoading ? (
                  <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>Gerando...</>
                ) : (
                  <><Eye className="w-4 h-4 mr-2" />Visualizar Prévia</>
                )}
              </Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={uploading} data-testid="template-submit-button">
                {uploading ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Enviando...</>) :
                  (<><Upload className="w-4 h-4 mr-2" />{editingTemplate ? 'Salvar' : 'Criar Modelo'}</>)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Excluir */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Confirmar Exclusão</DialogTitle>
            <DialogDescription>Tem certeza que deseja excluir o modelo <strong>{deleteTarget?.name}</strong>?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.template_id)} data-testid="confirm-delete-template">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tutorial Dialog */}
      <Dialog open={tutorialOpen} onOpenChange={setTutorialOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tutorial - Como criar modelos .docx</DialogTitle>
          </DialogHeader>
          <TutorialContent />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open && previewUrl) {
            window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#AF2628]" />
              Prévia do Modelo — {previewTitle}
            </DialogTitle>
            <DialogDescription>
              Renderização com dados de exemplo (cliente, itens e variáveis fictícios) para validação visual do layout.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 border border-[#E5E5EA] rounded-lg overflow-hidden bg-[#F7F7F8]">
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Prévia do modelo"
                className="w-full h-full"
                data-testid="template-preview-iframe"
              />
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="close-preview-button">
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
